import { useState, useEffect, useRef, useCallback } from 'react';
import { FaBell, FaCheck, FaTrash, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import toast from 'react-hot-toast';

/**
 * NotificationBell - Fixed responsive width issues
 * Now uses consistent Tailwind dark mode classes
 */
export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const UNREAD_CACHE_TTL_MS = 1000 * 15; // 15 seconds
  const cacheRef = useRef({ cache: null, promise: null });

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationsAPI.getAll({ limit: 10 });
      setNotifications(response.data.notifications || []);
      const totalUnread = response.data.unreadCount || 0;
      setUnreadCount(totalUnread);
      cacheRef.current.cache = {
        count: totalUnread,
        expiry: Date.now() + UNREAD_CACHE_TTL_MS,
      };
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [UNREAD_CACHE_TTL_MS]);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async () => {
    const now = Date.now();

    if (cacheRef.current.cache && cacheRef.current.cache.expiry > now) {
      setUnreadCount(cacheRef.current.cache.count);
      return;
    }

    if (!cacheRef.current.promise) {
      cacheRef.current.promise = notificationsAPI
        .getUnreadCount()
        .then((response) => {
          const count = response.data.count || 0;
          cacheRef.current.cache = {
            count,
            expiry: Date.now() + UNREAD_CACHE_TTL_MS,
          };
          return count;
        })
        .catch((error) => {
          cacheRef.current.cache = null;
          throw error;
        })
        .finally(() => {
          cacheRef.current.promise = null;
        });
    }

    try {
      const count = await cacheRef.current.promise;
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [UNREAD_CACHE_TTL_MS]);

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Mark notification as read
  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      await fetchNotifications();
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    if (notifications.length === 0) return;

    setLoading(true);
    try {
      await notificationsAPI.markAllAsRead();
      await fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    } finally {
      setLoading(false);
    }
  };

  // Delete notification
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationsAPI.delete(id);
      await fetchNotifications();
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    const iconClass = 'text-sm';
    switch (type) {
      case 'comment':
      case 'reply':
        return <span className={iconClass}>💬</span>;
      case 'rating':
        return <span className={iconClass}>⭐</span>;
      case 'follow':
        return <span className={iconClass}>👤</span>;
      case 'badge':
        return <span className={iconClass}>🏆</span>;
      case 'weekly_top':
        return <span className={iconClass}>🔥</span>;
      default:
        return <span className={iconClass}>🔔</span>;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
        aria-label="Notifications"
      >
        <FaBell className="text-xl" />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - FIXED: Now responsive width */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-lg shadow-xl z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={loading}
                    className="text-sm px-2 py-1 rounded transition-colors text-purple-600 dark:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Mark all as read"
                  >
                    <FaCheck />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-sm px-2 py-1 rounded transition-colors text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <FaBell className="text-4xl mx-auto mb-2 text-gray-400 dark:text-gray-600" />
                <p className="text-gray-600 dark:text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="relative border-b last:border-b-0 border-gray-200 dark:border-gray-700"
                >
                  <Link
                    to={notification.link || '#'}
                    onClick={() => {
                      if (!notification.is_read) {
                        handleMarkAsRead(notification.id);
                      }
                      setIsOpen(false);
                    }}
                    className={`block p-4 transition-colors ${
                      !notification.is_read
                        ? 'bg-purple-50 dark:bg-gray-750 hover:bg-purple-100 dark:hover:bg-gray-700'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 dark:text-white">
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs mt-1 text-gray-500 dark:text-gray-500">
                          {formatTimestamp(notification.created_at)}
                        </p>
                      </div>

                      {/* Unread indicator */}
                      {!notification.is_read && (
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(notification.id, e)}
                    className="absolute top-2 right-2 p-1 rounded transition-opacity opacity-0 hover:opacity-100 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Delete notification"
                  >
                    <FaTrash className="text-xs" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer - View All Link */}
          {notifications.length > 0 && (
            <div className="p-3 text-center border-t border-gray-200 dark:border-gray-700">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="text-sm font-medium transition-colors text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
